import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faClipboardList,
  faGlobe,
  faUsers,
  faUserPlus,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
};

const QuickActions = ({ nextEventDate, nextEventId }) => {
  const navigate = useNavigate();
  const timeLeft = useCountdown(nextEventDate);

  const hasTimeLeft =
    nextEventDate &&
    (timeLeft.days > 0 ||
      timeLeft.hours > 0 ||
      timeLeft.minutes > 0 ||
      timeLeft.seconds > 0);

  const actions = [
    {
      label: "Set Lineup",
      description: "Lock in your picks",
      icon: faBolt,
      onClick: () =>
        navigate(nextEventId ? `/events/${nextEventId}` : "/events"),
    },
    {
      label: "Scores",
      description: "View your scorecard",
      icon: faClipboardList,
      onClick: () => navigate("/scorecard"),
    },
    {
      label: "Global Competition",
      description: "Compete worldwide",
      icon: faGlobe,
      onClick: () => navigate("/globalcompetition"),
    },
    {
      label: "Create League",
      description: "Start with friends",
      icon: faUsers,
      onClick: () => navigate("/create-league"),
    },
    {
      label: "Join League",
      description: "Enter a code",
      icon: faUserPlus,
      onClick: () => navigate("/leagues"),
    },
  ];

  return (
    <section>
      <div className="mb-3 md:mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-dark-grey md:text-xl">
            Quick Actions
          </h2>
          <p className="mt-0.5 text-xs text-medium-grey">
            Jump right into the action
          </p>
        </div>

        {/* Event countdown timer */}
        {hasTimeLeft && (
          <div className="flex flex-col items-end">
            <span className="text-[10px] font-medium text-medium-grey uppercase tracking-wide mb-1">
              Event starts
            </span>
            <div className="flex items-center gap-1 md:gap-1.5">
              {[
                { value: timeLeft.days, label: "D" },
                { value: timeLeft.hours, label: "H" },
                { value: timeLeft.minutes, label: "M" },
                { value: timeLeft.seconds, label: "S" },
              ].map((item, index) => (
                <div
                  key={item.label}
                  className="flex items-center gap-1 md:gap-1.5"
                >
                  <div className="flex flex-col items-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-deep-red text-white md:h-9 md:w-9">
                      <span className="text-sm font-bold tabular-nums md:text-base">
                        {String(item.value).padStart(2, "0")}
                      </span>
                    </div>
                    <span className="mt-0.5 text-[9px] text-medium-grey md:text-[10px]">
                      {item.label}
                    </span>
                  </div>
                  {index < 3 && (
                    <span className="mb-3 text-sm font-light text-medium-grey/50">
                      :
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Horizontal scroll on mobile, grid on desktop */}
      <div className="relative md:static">
        {/* Fade hint on right edge - signals more content to scroll */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-light-grey to-transparent z-10 md:hidden" />

        <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-3 snap-x snap-mandatory md:mx-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:px-0 md:pb-0 md:snap-none quick-actions-scroll">
          {actions.map((action) => (
            <button
              key={action.label}
              onClick={action.onClick}
              className="group flex h-auto min-w-[140px] shrink-0 snap-start flex-col items-start gap-2 rounded-xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all active:scale-[0.98] md:min-w-0 md:gap-3 md:p-5 md:hover:border-deep-red/30 md:hover:shadow-card"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-deep-red/10 text-deep-red transition-colors md:group-hover:bg-deep-red md:group-hover:text-white">
                <FontAwesomeIcon icon={action.icon} className="w-5 h-5" />
              </div>
              <div>
                <span className="text-sm font-semibold text-dark-grey md:text-base">
                  {action.label}
                </span>
                <p className="mt-0.5 text-xs text-medium-grey">
                  {action.description}
                </p>
              </div>
              <FontAwesomeIcon
                icon={faArrowRight}
                className="hidden h-4 w-4 text-medium-grey transition-transform group-hover:translate-x-1 group-hover:text-deep-red md:block"
              />
            </button>
          ))}
        </div>

        {/* Thin styled scrollbar visible on mobile */}
        <style>{`
          .quick-actions-scroll {
            scrollbar-width: thin;
            scrollbar-color: #C41E3A20 transparent;
          }
          .quick-actions-scroll::-webkit-scrollbar {
            height: 4px;
          }
          .quick-actions-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .quick-actions-scroll::-webkit-scrollbar-thumb {
            background-color: rgba(196, 30, 58, 0.2);
            border-radius: 4px;
          }
          .quick-actions-scroll::-webkit-scrollbar-thumb:active {
            background-color: rgba(196, 30, 58, 0.4);
          }
          @media (min-width: 768px) {
            .quick-actions-scroll::-webkit-scrollbar {
              display: none;
            }
            .quick-actions-scroll {
              scrollbar-width: none;
            }
          }
        `}</style>
      </div>
    </section>
  );
};

export default QuickActions;
